import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import globalStyles from './styles/outline-stat.global.css?inline';
import encapsulatedStyles from './styles/outline-stat.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-stat';

/**
 *
 * A reusable LitElement component for displaying stats.
 *
 * @element outline-stat
 * @extends LitElement
 */
@customElement(componentName)
export class OutlineStat extends LitElement {
  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * Current count.
   */
  @property({ type: Number })
  current = 0;

  /**
   * Final value to display. ex: 100, $500, 800+.
   */
  @property({ type: String, attribute: 'stat-value' })
  statValue: string;

  /**
   * Short description text
   */
  @property({ type: String })
  description = '';

  /**
   * Short description text 2
   */
  @property({ type: String })
  description2 = '';

  /**
 * Wrapper class
 */
  @property({ type: String })
  class = '';

  /**
   * duration in ms.
   */
  @property({ type: Number })
  duration = 2000;

  /**
   * Wether or not the counter is scrolled into view. Set by component.
   */
  @property({ type: Boolean })
  visible = false;

  /**
   * Modifier like "k" or "$" to add to the display.
   */
  modifier = '';
  /**
   * Number to count to
   */
  end = 0;
  /**
   * Prevents annimation if prefers-reduced-motion.
   */
  preventAnimation: boolean;

  /**
   * has non-numeric chars in this.statValue
   */
  hasMod: boolean;

  /**
   * If modifier comes before the number. For use in cases like $500.
   */
  modFirst: boolean;

  // Animation Specific Values
  /**
   * amount to increment count per frame
   */
  increment = 1;

  /**
   * current frame
   */
  frame = 0;

  /**
   * MS delay between frames
   */
  delay: number;

  /**
   * total number of frames
   */
  totalFrames: number;

  render(): TemplateResult {
    return html`
      <div class="outline-counter ${this.class}">
        <div class="outline-counter--wrapper">
          <div class="outline-counter--top-content">${this.counterTemplate()}</div>
          <span class="outline-counter--description">
            <span class="visually-hidden">${this.statValue}</span>
            ${this.description}
          </span>
          <span class="outline-counter--description">
            ${this.description2}
          </span>
          <slot name="cta"></slot>
        </div>
      </div>
    `;
  }

  firstUpdated() {
    this.parseDisplay();
    this.setPreventAnimation();
    this.setTotalFrames();
    this.setInitialDelay();
    this.setInitialIncrement();
    this.isElementXPercentInViewport();
    window.addEventListener('scroll', this.isElementXPercentInViewport);
  }

  updated() {
    this.animateCounter();
  }

  disconnectedCallback(): void {
    window.removeEventListener('scroll', this.isElementXPercentInViewport);
  }

  setPreventAnimation() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.current = this.end;
      this.preventAnimation = true;
    }
  }

  counterTemplate() {
    if (!this.hasMod) {
      return html` <span class="outline-counter--count" aria-hidden="true"
        >${this.current}</span
      >`;
    }
    if (this.hasMod && this.modFirst) {
      return html`<span class="outline-counter--modifier" aria-hidden="true"
          >${this.modifier.toUpperCase()}</span
        >
        <span class="outline-counter--count" aria-hidden="true"
          >${this.current}</span
        >`;
    } else {
      return html`<span class="outline-counter--count" aria-hidden="true"
          >${this.current}</span
        >
        <span class="outline-counter--modifier" aria-hidden="true"
          >${this.modifier.toUpperCase()}</span
        >`;
    }
  }

  animateCounter() {
    if (!this.visible || this.preventAnimation) {
      return;
    }

    if (this.visible) {
      const timer = setTimeout(() => {
        if (this.current >= this.end) {
          clearTimeout(timer);
        } else {
          this.advanceFrame();
        }
      }, this.delay);
    }
  }

  advanceFrame() {
    if (this.frame === this.totalFrames - 5) {
      this.increment = 1;
      this.current = this.end - 5;
      // End of count slow down is a nice-to-have 
      // this.delay = this.delay * 1.5;
    }
    this.frame++;
    this.current += this.increment;
    if (this.current > this.end) {
      this.current = this.end;
    }
  }

  parseDisplay() {
    const parsedArray = this.statValue.split(/(\d+\.\d+|\d+)/g).filter(index => index !== '')
    // looks for non-numeric values in the parrsedArray and returns their index position or false if there are none.
    const findNonNumericalIndex = (arr: string[]): number | false => {
      const index = arr.findIndex(str => !/^\d+(\.\d+)?$/.test(str));
      return index !== -1 ? index : false;
    };

    const hasMod = findNonNumericalIndex(parsedArray);
    if (!hasMod && !isNaN(Number(this.end))) {
      this.end = Number(this.statValue);
      this.hasMod = false;
    }

    if (hasMod !== false) {
      if (hasMod && hasMod > 0) {
        this.end = Number(parsedArray[0]);
        this.modifier = parsedArray[1];
        this.hasMod = true;
      }
      if (hasMod === 0) {
        this.modifier = parsedArray[0]
        this.end = Number(parsedArray[1]);
        this.hasMod = true;
        this.modFirst = true;
      }
    }
  }

  setInitialIncrement() {
    this.increment = Math.max(Math.round(this.end / this.totalFrames), 1);
  }

  setTotalFrames() {
    const base = (this.duration / 1000) * 24;
    if (this.end > base) {
      this.totalFrames = (this.duration / 1000) * 24;
    } else {
      this.totalFrames = this.end;
    }
  }

  setInitialDelay() {
    this.delay = Math.max(Math.round(this.duration / this.totalFrames), 1);
  }

  /**
   * adapted from https://stackoverflow.com/a/51121566
   */
  isElementXPercentInViewport = () => {
    const rect = this.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    // if greater than X% set to visible and begin count.
    const revealPercentage = 50;
    return (this.visible = !(
      Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100) <
      revealPercentage ||
      Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) <
      revealPercentage
    ));
  };
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineStat;
  }
}
